import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import storageService from './storageService';

class ValidationService {
  // Validate một giá trị theo rule
  validateValue(value, rule) {
    const { ruleType, config } = rule;

    switch (ruleType) {
      case 'required':
        return this.validateRequired(value, config);
      
      case 'dataType':
        return this.validateDataType(value, config);
      
      case 'range':
        return this.validateRange(value, config);
      
      case 'regex':
        return this.validateRegex(value, config);
      
      case 'length':
        return this.validateLength(value, config);
      
      case 'custom':
        return this.validateCustom(value, config);
      
      default:
        return { valid: true };
    }
  }

  validateRequired(value, config) {
    const isEmpty = value === null || value === undefined || value === '';
    return {
      valid: !isEmpty,
      message: isEmpty ? (config.message || 'Trường này là bắt buộc') : null
    };
  }

  validateDataType(value, config) {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Skip if empty
    }

    const { type } = config;
    let valid = false;

    switch (type) {
      case 'number':
        valid = !isNaN(value) && !isNaN(parseFloat(value));
        break;
      case 'string':
        valid = typeof value === 'string' || typeof value === 'number';
        break;
      case 'date':
        valid = !isNaN(Date.parse(value));
        break;
      case 'boolean':
        valid = typeof value === 'boolean' || value === 'true' || value === 'false' || value === 0 || value === 1;
        break;
      default:
        valid = true;
    }

    return {
      valid,
      message: valid ? null : (config.message || `Giá trị phải là kiểu ${type}`)
    };
  }

  validateRange(value, config) {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Skip if empty
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return {
        valid: false,
        message: 'Giá trị không phải là số'
      };
    }

    const { min, max } = config;
    let valid = true;
    let message = null;

    if (min !== undefined && numValue < min) {
      valid = false;
      message = `Giá trị phải >= ${min}`;
    }

    if (max !== undefined && numValue > max) {
      valid = false;
      message = `Giá trị phải <= ${max}`;
    }

    return {
      valid,
      message: message || config.message
    };
  }

  validateRegex(value, config) {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Skip if empty
    }

    const { pattern } = config;
    const regex = new RegExp(pattern);
    const valid = regex.test(String(value));

    return {
      valid,
      message: valid ? null : (config.message || 'Giá trị không đúng định dạng')
    };
  }

  validateLength(value, config) {
    if (value === null || value === undefined || value === '') {
      return { valid: true }; // Skip if empty
    }

    const strValue = String(value);
    const { min, max, exact } = config;
    let valid = true;
    let message = null;

    if (exact !== undefined && strValue.length !== exact) {
      valid = false;
      message = `Độ dài phải là ${exact} ký tự`;
    }

    if (min !== undefined && strValue.length < min) {
      valid = false;
      message = `Độ dài tối thiểu ${min} ký tự`;
    }

    if (max !== undefined && strValue.length > max) {
      valid = false;
      message = `Độ dài tối đa ${max} ký tự`;
    }

    return {
      valid,
      message: message || config.message
    };
  }

  validateCustom(value, config) {
    try {
      // eslint-disable-next-line no-new-func
      const func = new Function('value', config.code);
      const result = func(value);
      
      return {
        valid: result === true,
        message: result === true ? null : (config.message || 'Validation failed')
      };
    } catch (error) {
      return {
        valid: false,
        message: `Lỗi validation: ${error.message}`
      };
    }
  }

  // Validate toàn bộ file
  async validateFile(file) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    let dataByEntity = {};

    // Parse file
    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      dataByEntity = await this.parseExcel(file);
    } else if (fileExtension === 'csv') {
      dataByEntity = await this.parseCSV(file);
    } else {
      throw new Error('Unsupported file type');
    }

    // Get validation rules
    const rules = storageService.getValidationRules();
    const entities = storageService.getDataEntities();

    // Validate each entity
    const results = [];

    for (const [entityName, rows] of Object.entries(dataByEntity)) {
      const entity = entities.find(e => 
        e.name === entityName || e.code === entityName
      );

      if (!entity) {
        console.warn(`Entity not found: ${entityName}`);
        continue;
      }

      // Get rules for this entity
      const entityRules = rules.filter(r => r.entityId === entity.id && r.enabled);

      // Validate each row
      rows.forEach((row, rowIndex) => {
        const rowValidations = [];

        // Validate each field that has rules
        entityRules.forEach(rule => {
          const field = entity.fields.find(f => f.id === rule.fieldId);
          if (!field) return;

          const value = row[field.code] || row[field.name];
          const validation = this.validateValue(value, rule);

          if (!validation.valid) {
            rowValidations.push({
              row: rowIndex + 2, // +2 for header and 0-index
              column: field.name,
              columnCode: field.code,
              value: value,
              rule: rule.ruleName,
              message: validation.message,
              status: 'FAIL'
            });
          }
        });

        if (rowValidations.length > 0) {
          results.push({
            entity: entity.name,
            entityCode: entity.code,
            validations: rowValidations
          });
        }
      });
    }

    return {
      fileName: file.name,
      fileSize: file.size,
      entities: Object.keys(dataByEntity),
      totalRows: Object.values(dataByEntity).reduce((sum, rows) => sum + rows.length, 0),
      validationResults: results,
      hasErrors: results.length > 0,
      validatedAt: new Date().toISOString()
    };
  }

  async parseExcel(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const result = {};
          
          workbook.SheetNames.forEach(sheetName => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            result[sheetName] = jsonData;
          });
          
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  async parseCSV(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const entityName = file.name.replace('.csv', '');
          resolve({ [entityName]: results.data });
        },
        error: reject
      });
    });
  }

  // Export validation results to Excel
  exportResults(validationResult) {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summary = [
      ['Tên file', validationResult.fileName],
      ['Kích thước', `${(validationResult.fileSize / 1024).toFixed(2)} KB`],
      ['Số dòng', validationResult.totalRows],
      ['Đối tượng', validationResult.entities.join(', ')],
      ['Trạng thái', validationResult.hasErrors ? 'CÓ LỖI' : 'HỢP LỆ'],
      ['Thời gian', new Date(validationResult.validatedAt).toLocaleString('vi-VN')]
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Tổng quan');

    // Errors sheet
    if (validationResult.validationResults.length > 0) {
      const errors = [];
      errors.push(['Đối tượng', 'Dòng', 'Cột', 'Giá trị', 'Quy tắc', 'Lỗi']);

      validationResult.validationResults.forEach(result => {
        result.validations.forEach(v => {
          errors.push([
            result.entity,
            v.row,
            v.column,
            v.value,
            v.rule,
            v.message
          ]);
        });
      });

      const errorsSheet = XLSX.utils.aoa_to_sheet(errors);
      XLSX.utils.book_append_sheet(wb, errorsSheet, 'Chi tiết lỗi');
    }

    XLSX.writeFile(wb, `validation_result_${Date.now()}.xlsx`);
  }
}

export default new ValidationService();
